import type { BrowserOptions, Breadcrumb, BreadcrumbHint, ErrorEvent, Event, EventHint, StackFrame } from '@sentry/react';

type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];

/**
 * Exclusion-list privacy filter. The SDK event is kept (real message, route, page URL, user id/role, client IP,
 * breadcrumbs, browser info); what is removed or masked is listed explicitly below.
 */

const EMAIL = /[\w.+%-]+@[\w-]+(?:\.[\w-]+)+/g;
// 9 to 15 digits (optional +/00 prefix and separators). Not preceded/followed by word chars or '-' so that
// identifiers such as UUIDs or timestamps embedded in tokens are left alone.
const PHONE = /(?<![\w-])(?:\+|00)?\d(?:[\s.()-]?\d){8,14}(?![\w-])/g;
const JWT = /\beyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const ABSOLUTE_URL = /\bhttps?:\/\/[^\s"'<>)]+/g;

/** Strip query string and fragment from a URL or path, keeping origin and path. */
export const stripUrl = (value: string | undefined): string | undefined => {
  if (!value) return value;
  return value.split(/[?#]/, 1)[0];
};

/** Mask emails, phone numbers and JWTs, and drop query/fragment from URLs embedded in free text. */
export const maskPii = (text: string): string =>
  text
    .replace(ABSOLUTE_URL, (url) => stripUrl(url) ?? url)
    .replace(JWT, '[token]')
    .replace(EMAIL, '[email]')
    .replace(PHONE, '[phone]');

const maskText = (value: string | undefined, max?: number): string | undefined => {
  if (value === undefined) return value;
  const masked = maskPii(value);
  return max && masked.length > max ? `${masked.slice(0, max - 3)}...` : masked;
};

/** URL or path with params and fragment removed and any email/phone in the path masked. */
const cleanUrl = (value: string | undefined): string | undefined => maskText(stripUrl(value));

const safeCodeName = (value: string | undefined): string | undefined =>
  value && /^[\w.$<>[\] /:-]{1,250}$/.test(value) ? value : undefined;

const codeFile = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  const path = value.split(/[?#]/, 1)[0];
  // Only application bundles are useful for resolving our source maps.
  return /^(?:https:\/\/(?:www\.)?bricolat\.co\.ma)?\/assets\/[\w./-]+\.js$/.test(path) ? path : undefined;
};

export const siteAssetUrl = /^https:\/\/(?:www\.)?bricolat\.co\.ma\/assets\//;

const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk .* failed/;

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'ChunkLoadError' || CHUNK_LOAD_ERROR.test(error.message));

const isAxiosError = (error: unknown): boolean =>
  error instanceof Error && (error.name === 'AxiosError' || (error as { isAxiosError?: boolean }).isAxiosError === true);

/** Noise that would burn the error quota: foreign scripts, stale-chunk reloads, handled API failures. */
const isNoise = (event: ErrorEvent, hint?: EventHint): boolean => {
  const original = hint?.originalException;
  if (isChunkLoadError(original) || isAxiosError(original)) return true;
  const exceptions = event.exception?.values ?? [];
  if (exceptions.some((exception) => CHUNK_LOAD_ERROR.test(`${exception.type ?? ''} ${exception.value ?? ''}`))) return true;
  return !exceptions.some((exception) =>
    exception.stacktrace?.frames?.some((frame) => /\/assets\//.test(frame.filename?.split(/[?#]/, 1)[0] ?? '')));
};

const cleanFrame = (frame: StackFrame): StackFrame => ({
  filename: codeFile(frame.filename),
  function: safeCodeName(frame.function),
  lineno: frame.lineno,
  colno: frame.colno,
  in_app: frame.in_app,
  debug_id: frame.debug_id,
});

// ---- Breadcrumbs -------------------------------------------------------------------------------------------

const BREADCRUMB_TEXT_MAX = 100;
const KEPT_BREADCRUMB_CATEGORIES = new Set(['navigation', 'ui.click', 'fetch', 'xhr', 'console']);

type Data = Record<string, unknown>;
const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

const TEXT_TARGETS = 'button, a, [role="button"], [role="link"]';
const FIELDS = 'input, textarea, select, [contenteditable]';

const clickTargetText = (event: unknown): string | undefined => {
  const target = (event as { target?: unknown } | undefined)?.target;
  if (typeof Element === 'undefined' || !(target instanceof Element)) return undefined;
  const control = target.closest(TEXT_TARGETS);
  // Never read fields: inputs, textareas, selects and editable content may hold what the user typed.
  // Containers marked data-sentry-mask (private message content) never contribute click text.
  if (!control || control.closest('[data-sentry-mask]') || control.matches(FIELDS) || control.querySelector(FIELDS)) return undefined;
  const text = (control.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text ? maskText(text, 60) : undefined;
};

/**
 * Keeps navigation, clicks, fetch/XHR and console errors only; every other category (keypress/input, SDK-internal)
 * is dropped. Input values are never read.
 */
export const sanitizeBreadcrumb = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null => {
  const category = breadcrumb.category ?? '';
  if (!KEPT_BREADCRUMB_CATEGORIES.has(category)) return null;
  const data = (breadcrumb.data ?? {}) as Data;

  if (category === 'console') {
    if (breadcrumb.level !== 'error') return null;
    return { type: breadcrumb.type, category, level: 'error', timestamp: breadcrumb.timestamp,
      message: maskText(breadcrumb.message, 300) };
  }

  if (category === 'navigation') {
    return { type: breadcrumb.type, category, timestamp: breadcrumb.timestamp,
      data: { from: cleanUrl(asString(data.from)), to: cleanUrl(asString(data.to)) } };
  }

  if (category === 'fetch' || category === 'xhr') {
    return { type: 'http', category, level: breadcrumb.level, timestamp: breadcrumb.timestamp,
      data: { method: asString(data.method), url: cleanUrl(asString(data.url)),
        status_code: typeof data.status_code === 'number' ? data.status_code : undefined } };
  }

  // ui.click: selector built by the SDK (tag/classes/ids, never field values) + button/link text, masked and truncated.
  const cleaned: Breadcrumb = { type: breadcrumb.type, category, timestamp: breadcrumb.timestamp,
    message: maskText(breadcrumb.message, BREADCRUMB_TEXT_MAX) };
  const text = clickTargetText(hint?.event) ?? asString(data.text);
  if (text) cleaned.data = { text: maskText(text, 60) };
  return cleaned;
};

// ---- Events ------------------------------------------------------------------------------------------------

const NEVER_MASK_KEYS = new Set(['event_id', 'debug_id', 'trace_id', 'span_id', 'parent_span_id', 'release', 'environment', 'type']);
const URL_KEYS = /(^|[._-])(url|href|uri|target|from|to)$/i;
const SENSITIVE_KEYS = /^(cookies?|set-cookie|authorization|password|passwd|token|access_token|refresh_token|query_string|http\.query|http\.fragment|url\.query|url\.fragment|http\.request\.header.*|http\.response\.header.*)$/i;

/** Deep pass over free-form containers (contexts, tags, span data): drops sensitive keys, strips URL params, masks strings. */
const scrub = (value: unknown, key = ''): unknown => {
  if (typeof value === 'string') {
    if (NEVER_MASK_KEYS.has(key)) return value;
    return URL_KEYS.test(key) ? cleanUrl(value) : maskPii(value);
  }
  if (Array.isArray(value)) return value.map((item) => scrub(item, key));
  if (value && typeof value === 'object') {
    const result: Data = {};
    for (const [childKey, child] of Object.entries(value as Data)) {
      if (SENSITIVE_KEYS.test(childKey)) continue;
      result[childKey] = scrub(child, childKey);
    }
    return result;
  }
  return value;
};

const cleanUser = (user: Event['user']): Event['user'] => {
  if (!user) return undefined;
  const { id, ip_address: ip, role } = user as { id?: unknown; ip_address?: unknown; role?: unknown };
  const clean: Data = {};
  if (id !== undefined && id !== null) clean.id = String(id);
  if (typeof role === 'string') clean.role = role;
  if (typeof ip === 'string') clean.ip_address = ip;
  return Object.keys(clean).length ? clean : undefined;
};

const cleanRequest = (request: Event['request']): Event['request'] => {
  if (!request) return undefined;
  const userAgent = request.headers
    ? Object.entries(request.headers).find(([name]) => name.toLowerCase() === 'user-agent')?.[1]
    : undefined;
  return {
    url: cleanUrl(request.url),
    method: request.method,
    headers: userAgent ? { 'User-Agent': userAgent } : undefined,
  };
};

const cleanContexts = (contexts: Event['contexts']): Event['contexts'] => {
  if (!contexts) return undefined;
  // `response` carries headers/cookies/status of captured responses; `state` can hold application data.
  const rest: Data = { ...contexts };
  delete rest.response;
  delete rest.state;
  return scrub(rest) as Event['contexts'];
};

const cleanBreadcrumbs = (breadcrumbs: Breadcrumb[] | undefined): Breadcrumb[] | undefined =>
  breadcrumbs?.flatMap((breadcrumb) => {
    const cleaned = sanitizeBreadcrumb(breadcrumb);
    return cleaned ? [cleaned] : [];
  });

/** Fields shared by error and transaction events. */
const sanitizeCommon = (event: Event): Event => ({
  event_id: event.event_id,
  timestamp: event.timestamp,
  start_timestamp: event.start_timestamp,
  platform: 'javascript',
  level: event.level,
  release: event.release,
  environment: event.environment,
  dist: event.dist,
  sdk: event.sdk,
  tags: { ...(scrub(event.tags) as Event['tags']), application: 'bricolat-web' },
  transaction: cleanUrl(event.transaction),
  user: cleanUser(event.user),
  request: cleanRequest(event.request),
  contexts: cleanContexts(event.contexts),
  breadcrumbs: cleanBreadcrumbs(event.breadcrumbs),
  // Intentionally not copied: extra, cookies, request data/query_string, server_name, modules, response contexts.
});

export const sanitizeErrorEvent = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  if (!event.exception?.values?.length || isNoise(event, hint)) return null;
  return {
    ...sanitizeCommon(event),
    type: undefined,
    message: maskText(event.message),
    logentry: event.logentry ? { message: maskText(event.logentry.message), params: undefined } : undefined,
    exception: {
      values: event.exception.values.map((exception) => ({
        type: exception.type,
        value: maskText(exception.value),
        mechanism: exception.mechanism ? {
          type: safeCodeName(exception.mechanism.type) ?? 'generic',
          handled: exception.mechanism.handled,
        } : undefined,
        stacktrace: exception.stacktrace ? {
          frames: exception.stacktrace.frames?.map(cleanFrame),
        } : undefined,
      })),
    },
    debug_meta: event.debug_meta ? {
      images: event.debug_meta.images?.flatMap((image) => {
        const file = codeFile(image.code_file);
        return image.type === 'sourcemap' && file
          ? [{ type: 'sourcemap' as const, debug_id: image.debug_id, code_file: file }] : [];
      }),
    } : undefined,
  } as ErrorEvent;
};

const SPAN_URL_ATTRIBUTES = /(^|[._])(url|href|full|target|path|original)$/i;

const cleanSpanData = (data: Data | undefined): Data | undefined => {
  if (!data) return undefined;
  const result: Data = {};
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_KEYS.test(key) || /\.(query|fragment)$/.test(key)) continue;
    if (typeof value === 'string') result[key] = SPAN_URL_ATTRIBUTES.test(key) ? cleanUrl(value) : maskPii(value);
    else result[key] = value;
  }
  return result;
};

/** "GET https://host/api?x=1" -> "GET https://host/api"; also masks emails and phones. */
const cleanSpanDescription = (description: string | undefined): string | undefined =>
  description === undefined ? undefined : maskPii(description.replace(/(\S+?)[?#]\S*/g, '$1'));

/** Transactions: route-pattern name, page URL without params, span URLs without params, no query data. */
export const sanitizeTransactionEvent = (event: TransactionEvent): TransactionEvent => {
  const common = sanitizeCommon(event);
  const trace = common.contexts?.trace;
  return {
    ...common,
    type: 'transaction',
    transaction_info: event.transaction_info,
    measurements: event.measurements,
    contexts: common.contexts && trace
      ? { ...common.contexts, trace: { ...trace, data: cleanSpanData(trace.data as Data | undefined) } as typeof trace }
      : common.contexts,
    spans: event.spans?.map((span) => ({
      ...span,
      description: cleanSpanDescription(span.description),
      data: cleanSpanData(span.data as Data | undefined) as typeof span.data,
    })),
  } as TransactionEvent;
};
